import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { useSnackbar } from 'hooks/use-snackbar';

interface CreateCircuitDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (warehouseId: string, name: string, priority: number) => void;
  loading?: boolean;
  preselectedWarehouseId?: string | null;
  preselectedWarehouseName?: string;
}

const CreateCircuitDialog: React.FC<CreateCircuitDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  preselectedWarehouseId,
  preselectedWarehouseName,
}) => {
  const [warehouseId, setWarehouseId] = useState('');
  const [name, setName] = useState('');
  const [priority, setPriority] = useState<number>(1);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const snackbar = useSnackbar();

  const fetchWarehouses = () => {
    setWarehousesLoading(true);
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
        setWarehousesLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
        snackbar.show('Falha ao carregar armazéns', null, 'error');
        setWarehousesLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchWarehouses();
      // Reset form
      setWarehouseId(preselectedWarehouseId || '');
      setName('');
      setPriority(1);
    }
  }, [open, preselectedWarehouseId]);

  const handleSubmit = () => {
    if (warehouseId && name && priority) {
      onSubmit(warehouseId, name, priority);
    }
  };

  const handleClose = () => {
    setWarehouseId(preselectedWarehouseId || '');
    setName('');
    setPriority(1);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Criar Circuito</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <FormControl fullWidth required>
            <InputLabel>Armazém</InputLabel>
            <Select
              value={warehouseId}
              label="Armazém"
              onChange={(e) => setWarehouseId(e.target.value)}
              disabled={loading || warehousesLoading}
            >
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField label="Nome" value={name} onChange={(e) => setName(e.target.value)} disabled={loading} required fullWidth />

          <TextField
            label="Prioridade"
            type="number"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            disabled={loading}
            required
            fullWidth
            inputProps={{ min: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!warehouseId || !name || !priority || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'A criar...' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCircuitDialog;
